import { Download, ThumbsDown, ThumbsUp } from "lucide-react";
import React, { useEffect, useState } from "react";


import Button from "../../lib/ui/components/button/Button";
import Modal from "../../lib/ui/components/Modal";
import Spinner from "../../lib/ui/components/spinner/Spinner";
import { Heading6, SmallText, XsmallText } from "../../lib/ui/components/typography/Typography";
import {
    GENERATE_CANDIDATE_RESUME_INSIGHTS,
    GET_CANDIDATE_RESUME,
    GET_CANDIDATE_RESUME_PDF,
} from "../../lib/urls";
import {
    resumeTextHasExternalLinks,
    splitResumeTextWithLinks,
    stripTrailingUrlPunctuation,
} from "../../utils/resumeTextUtils";

const ResumePdfPreview = ({ pdfUrl, fileName }) => (
    <iframe
        title={`${fileName || "Resume"} preview`}
        src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
        className="h-full min-h-0 w-full flex-1 rounded-lg border border-gray-200 bg-[#323639]"
    />
);

// The ATS doesn't always send a file name, and the resume isn't always a PDF.
const defaultResumeFileName = (contentType) => {
    const type = (contentType || "").toLowerCase();
    if (type.includes("wordprocessingml")) return "resume.docx";
    if (type.includes("msword")) return "resume.doc";
    return "resume.pdf";
};

// Only PDFs render inline in the browser, so Word resumes fall back to the
// text extracted during ATS sync.
const ResumeTextPreview = ({ text }) => (
    <div className="h-full min-h-0 w-full flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-white p-4">
        <SmallText weight="normal" emphasis={false} className="whitespace-pre-wrap">
            {splitResumeTextWithLinks(text).map(({ key, type, value }) => {
                if (type !== "link") {
                    return <React.Fragment key={key}>{value}</React.Fragment>;
                }
                const href = stripTrailingUrlPunctuation(value);
                const trailing = value.slice(href.length);
                return (
                    <React.Fragment key={key}>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="custom-link underline"
                        >
                            {href}
                        </a>
                        {trailing}
                    </React.Fragment>
                );
            })}
        </SmallText>
    </div>
);

/** Shared preview body for the nested drawer or the host applicant sidebar. */
export const ResumePreviewPanel = ({ preview }) => {
    if (!preview) return null;
    if (preview.mode === "pdf") {
        return (
            <ResumePdfPreview
                pdfUrl={preview.pdfUrl}
                fileName={preview.fileName}
            />
        );
    }
    if (preview.mode === "text") {
        return <ResumeTextPreview text={preview.text} />;
    }
    return (
        <Heading6>
            {preview.canDownload
                ? "Preview not available for this file type. Download the resume to view it."
                : "Resume preview is not available."}
        </Heading6>
    );
};

const authHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
});

const ResumeTabContent = ({
    candidateId,
    companyId,
    jobId,
    token,
    /** When set, Preview fills the host sidebar instead of opening a second drawer. */
    onOpenPreview,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [summaryPending, setSummaryPending] = useState(false);
    const [highlightsPending, setHighlightsPending] = useState(false);
    const [error, setError] = useState("");
    const [resume, setResume] = useState(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [isPdfFile, setIsPdfFile] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;

        const loadResume = async () => {
            if (!candidateId || !companyId || !jobId || !token) {
                setIsLoading(false);
                setError("Resume could not be loaded.");
                return;
            }

            setIsLoading(true);
            setIsFileLoading(true);
            setSummaryPending(false);
            setHighlightsPending(false);
            setError("");
            // Reset per-candidate state so a switch never leaves the preview
            // pointing at the previous candidate's (revoked) blob URL.
            setResume(null);
            setFileUrl(null);
            setIsPdfFile(false);
            setIsPreviewOpen(false);

            try {
                const detailsPromise = fetch(
                    GET_CANDIDATE_RESUME(companyId, jobId, candidateId),
                    { headers: authHeaders(token) }
                );
                const filePromise = fetch(
                    GET_CANDIDATE_RESUME_PDF(companyId, jobId, candidateId),
                    { headers: authHeaders(token) }
                );

                const detailsResponse = await detailsPromise;

                if (detailsResponse.status === 404) {
                    if (!cancelled) {
                        setResume({ has_resume: false, highlights: [] });
                        setIsFileLoading(false);
                    }
                    return;
                }

                if (!detailsResponse.ok) {
                    throw new Error("Failed to load resume");
                }

                const details = await detailsResponse.json();
                if (cancelled) return;

                setResume(details);
                setSummaryPending(Boolean(details.summary_pending));
                setHighlightsPending(Boolean(details.highlights_pending));
                setIsLoading(false);

                if (!details.has_resume) {
                    setIsFileLoading(false);
                    return;
                }

                const insightsPromise =
                    details.summary_pending || details.highlights_pending
                        ? fetch(
                              GENERATE_CANDIDATE_RESUME_INSIGHTS(
                                  companyId,
                                  jobId,
                                  candidateId
                              ),
                              {
                                  method: "POST",
                                  headers: authHeaders(token),
                              }
                          )
                        : null;

                try {
                    const fileResponse = await filePromise;
                    if (fileResponse.ok) {
                        const blob = await fileResponse.blob();
                        objectUrl = URL.createObjectURL(blob);
                        if (!cancelled) {
                            setFileUrl(objectUrl);
                            setIsPdfFile(
                                (blob.type || details.content_type || "")
                                    .toLowerCase()
                                    .includes("pdf")
                            );
                        } else {
                            URL.revokeObjectURL(objectUrl);
                        }
                    }
                } catch {
                    // Preview/Download stay disabled; the rest of the tab is up.
                }
                if (!cancelled) {
                    setIsFileLoading(false);
                }

                if (!insightsPromise) {
                    return;
                }

                try {
                    const insightsResponse = await insightsPromise;
                    if (!insightsResponse.ok) {
                        return;
                    }
                    const insights = await insightsResponse.json();
                    if (cancelled) return;
                    setResume((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  summary: insights.summary ?? prev.summary,
                                  highlights: insights.highlights ?? prev.highlights,
                              }
                            : prev
                    );
                } catch {
                    // Empty states render once pending flags clear.
                } finally {
                    if (!cancelled) {
                        setSummaryPending(false);
                        setHighlightsPending(false);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    setError("Failed to load resume.");
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsFileLoading(false);
                }
            }
        };

        loadResume();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [candidateId, companyId, jobId, token]);

    useEffect(() => {
        return () => {
            if (fileUrl) {
                URL.revokeObjectURL(fileUrl);
            }
        };
    }, [fileUrl]);

    if (isLoading) {
        return (
            <div className="p-4 flex items-center gap-3">
                <Heading6>Loading resume...</Heading6>
                <Spinner size="md" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <Heading6>{error}</Heading6>
            </div>
        );
    }

    if (!resume?.has_resume) {
        return (
            <div className="p-4">
                <Heading6>No resume available.</Heading6>
            </div>
        );
    }

    const highlights = resume.highlights || [];
    // The resume can come from this person's other application at the company,
    // in which case highlights are withheld because they score one role.
    const borrowedResumeNotice = resume.from_other_job
        ? `This resume comes from this candidate's other application${
              resume.source_job_role ? ` (${resume.source_job_role})` : ""
          }. Resume highlights aren't shown, since they're scored against a single role.`
        : "";
    const downloadFileName = resume.file_name || defaultResumeFileName(resume.content_type);
    const previewText = resume.extracted_text || "";
    const preferTextPreviewForLinks = resumeTextHasExternalLinks(previewText);
    const canDownload = Boolean(fileUrl);
    const canPreview =
        Boolean(previewText) || (isPdfFile && Boolean(fileUrl));

    const buildPreview = () => {
        const title = resume.file_name || "Resume preview";
        if (isPdfFile && fileUrl && !preferTextPreviewForLinks) {
            return {
                title,
                mode: "pdf",
                pdfUrl: fileUrl,
                fileName: resume.file_name,
                canDownload,
            };
        }
        if (previewText) {
            return {
                title,
                mode: "text",
                text: previewText,
                canDownload,
            };
        }
        return {
            title,
            mode: "unavailable",
            canDownload,
        };
    };

    const openPreview = () => {
        if (!canPreview) return;
        const preview = buildPreview();
        if (onOpenPreview) {
            onOpenPreview(preview);
            return;
        }
        setIsPreviewOpen(true);
    };

    const handleDownloadResume = () => {
        if (!fileUrl) return;
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-6">
            {borrowedResumeNotice && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <XsmallText weight="normal" emphasis={false}>
                        {borrowedResumeNotice}
                    </XsmallText>
                </div>
            )}

            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="space-y-1">
                        <Heading6>Resume Highlights</Heading6>
                        <XsmallText weight="normal" emphasis={false}>
                            How this resume matches the role.
                        </XsmallText>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {isFileLoading && <Spinner size="sm" />}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={openPreview}
                            disabled={!canPreview}
                        >
                            Resume Preview
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<Download size="1em" />}
                            onClick={handleDownloadResume}
                            disabled={!canDownload}
                        >
                            Download Resume
                        </Button>
                    </div>
                </div>
                {highlightsPending ? (
                    <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-4">
                        <Spinner size="md" />
                        <SmallText weight="normal" emphasis={false}>
                            Generating highlights...
                        </SmallText>
                    </div>
                ) : (
                    highlights.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                            {highlights.map((highlight, index) => {
                                const isMatch = highlight.type === "match";
                                return (
                                    <div
                                        key={`${highlight.title}-${index}`}
                                        className="bg-white p-3 rounded-lg border border-gray-200 h-full"
                                    >
                                        <div className="flex items-start gap-2">
                                            {isMatch ? (
                                                <ThumbsUp size="1em"
                                                    className="mt-0.5 shrink-0 text-green-600 text-small"
                                                    aria-label="Match"
                                                />
                                            ) : (
                                                <ThumbsDown size="1em"
                                                    className="mt-0.5 shrink-0 text-red-600 text-small"
                                                    aria-label="Non-match"
                                                />
                                            )}
                                            <div>
                                                <SmallText weight="semibold" emphasis={false} className="mb-1">
                                                    {highlight.title}
                                                </SmallText>
                                                <XsmallText weight="normal" emphasis={false}>
                                                    {highlight.detail}
                                                </XsmallText>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}
            </div>

            <div className="space-y-4">
                <div className="space-y-1">
                    <Heading6>Resume Summary</Heading6>
                    <XsmallText weight="normal" emphasis={false}>
                        A factual summary of the resume itself.
                    </XsmallText>
                </div>
                {summaryPending ? (
                    <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                        <Spinner size="md" />
                        <SmallText weight="normal" emphasis={false}>
                            Generating summary...
                        </SmallText>
                    </div>
                ) : resume.summary ? (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <SmallText weight="normal" emphasis={false}>
                            {resume.summary}
                        </SmallText>
                    </div>
                ) : (
                    <div className="p-4 rounded-lg border border-gray-200">
                        <Heading6>No resume summary available.</Heading6>
                    </div>
                )}
            </div>

            {!onOpenPreview && (
                <Modal
                    show={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    placement="right"
                    size="xl"
                    dismissible
                    className="right-drawer-wide"
                >
                    <Modal.Header className="resume-preview-drawer-header">
                        <div className="flex w-full min-w-0 items-center justify-between gap-3 pr-2">
                            <span className="truncate">
                                {resume.file_name || "Resume preview"}
                            </span>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
                        <ResumePreviewPanel preview={buildPreview()} />
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};

export default ResumeTabContent;
